import { Middleware } from "redux";
import transit from "transit-immutable-js";
import { setIsChildFrame } from "actions/scope";

export type CrossFrameAction<T = string, P = any> = {
    type: T;
    isCrossFrame?: boolean;
} & (P extends void | undefined | null
    ? {}
    : {
          payload: P;
      });

type PostData = {
    xType: string;
    xAction?: string;
};

const ACTION_TYPE = "@cross-frame-action";
const REGISTER_CHILD = "@cross-frame-register-child";
const UNREGISTER_CHILD = "@cross-frame-unregister-child";
const PARENT_CONFIRM = "@cross-frame-parent-confirm";

/**
 * Provides posting actions between frames.
 *
 * To make the action to be posted to another frame(s), the action must be {@link CrossFrameAction},
 * that is, to have its prop `isCrossFrame` equal `true`.
 *
 * There are some limitations:
 *
 * 1. this middleware should be applied both in parent app and the child(ren),
 * 2. naturally, the action to post must be serializable.
 *
 * See the simple proto: {@link https://plnkr.co/edit/uv76KFjjgpHSHqFy}
 */
const crossFrame: Middleware = (store) => {
    const children = [] as Window[];
    let confirmedParent: Window | undefined;

    /**
     * Posts the Action both to the confirmed parent and all registered children.
     */
    function crossDispatch(action: CrossFrameAction, skipChild?: Window) {
        const postData: PostData = {
            xType: ACTION_TYPE,
            xAction: transit.toJSON(action),
        };

        if (confirmedParent && confirmedParent !== skipChild) {
            confirmedParent.postMessage(postData, "*");
        }

        let i = 0;
        while (i < children.length) {
            const child = children[i];

            // As a precaution to avoid memory leaks, when unregistering was not done for some reason,
            // check the child window is still in the DOM. However, CORS might block access to the child's frameElement.
            // In such cases, the frameElement prop is null even if the frame element is still in DOM.
            // We cannot handle this super-edge case, plus it is virtually not possible in the application.
            if (child.frameElement && !child.frameElement.isConnected) {
                children.splice(i, 1);
            } else {
                if (child !== skipChild) {
                    child.postMessage(postData, "*");
                }
                i += 1;
            }
        }
    }

    function tryRegister() {
        // TODO: (open question) should we ever consider supporting a stack of parent windows.
        // That is, more than two redux application, which should communicate, in the iframe hierarchy.
        // Currently, if more than one redux upward, whoever gets confirmedParent once responds first.
        if (confirmedParent) {
            return;
        }

        let cur = window.parent;
        while (cur !== window) {
            cur.postMessage(
                {
                    xType: REGISTER_CHILD,
                } as PostData,
                "*"
            );
            if (cur.parent && cur.parent !== cur) {
                cur = cur.parent;
            } else {
                break;
            }
        }
    }

    window.addEventListener(
        "message",
        (e) => {
            const { xType, xAction } = e.data as PostData;
            const source = e.source as Window;

            switch (xType) {
                case ACTION_TYPE:
                    // re-dispatch the action, which was cross-posted from another frame.
                    const action = transit.fromJSON(xAction);
                    store.dispatch(action);
                    break;

                case REGISTER_CHILD:
                    if (!children.includes(source)) {
                        children.push(source);
                        source.postMessage(
                            {
                                xType: PARENT_CONFIRM,
                            } as PostData,
                            "*"
                        );
                    }
                    break;

                case UNREGISTER_CHILD:
                    const index = children.indexOf(source);
                    if (index >= 0) {
                        children.splice(index, 1);
                    }
                    break;

                case PARENT_CONFIRM:
                    confirmedParent = source;
                    // here, we're sure that we are not alone, and it's not the top frame.
                    store.dispatch(setIsChildFrame(true));
                    break;
            }
        },
        false
    );

    window.addEventListener("beforeunload", () => {
        if (confirmedParent) {
            confirmedParent.postMessage(
                {
                    xType: UNREGISTER_CHILD,
                } as PostData,
                "*"
            );
            confirmedParent = undefined;
        }
        children.length = 0;
    });

    tryRegister();

    return (next) => (action) => {
        tryRegister();

        const { isCrossFrame, ...noCrossAction } = (action || {}) as CrossFrameAction;
        if (isCrossFrame) {
            crossDispatch(noCrossAction);
        }

        return next(action);
    };
};

export default crossFrame;
